import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button } from '@cbrebuild/blocks';
import debounce from 'lodash/debounce';
import moment from 'moment';
import PropTypes from 'prop-types';
import EmptyState from '../../components/empty-state/empty-state';
import LoadingBar from '../../nucleus/loading-bar/loading-bar';
import userEventService from '../../services/user-event-service';
import {
  fetchPagedUserDealRestrictions,
  fetchCProUserAudit,
  updateUserDealRestrictions
} from '../../services/admin-service';
import './admin-manage-cpro-users.scss';
import { Style } from 'victory';

/**
 * AdminManageCProUsers
 * Lists CPro user deal type restrictions with search and pagination.
 */
const AdminManageCProUsers = ({ pageSize = 25, userData }) => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [userHistoryVisible, setUserHistoryVisible] = useState({}); // userId -> bool
  const [userAuditTrail, setUserAuditTrail] = useState({}); // userId -> audit data
  const [userAuditLoading, setUserAuditLoading] = useState({}); // userId -> bool
  const [userAuditPage, setUserAuditPage] = useState({}); // userId -> current page
  const [userAuditHasMore, setUserAuditHasMore] = useState({}); // userId -> bool
  const [userAuditTotal, setUserAuditTotal] = useState({}); // userId -> total count

  const restrictionUpdateTimer = useRef(null);
  const searchTimer = useRef(null);

  const loadUsers = async (nextPage = 1, currentSearch = '') => {
    setLoading(true);

    // Track CPro user search if search term is provided
    if (currentSearch && currentSearch.trim().length > 0 && nextPage === 1) {
      userEventService.trackEvent({
        eventCategory: 'Admin Capabilities',
        eventAction: 'admin_cpro_user_search',
        eventLabel: 'admin_manage_cpro_users',
      });
    }

    const resp = await fetchPagedUserDealRestrictions({
      page: nextPage,
      page_size: pageSize,
      search: currentSearch
    });
    
    let usersData = resp.results;
    
    if (nextPage === 1) {
      setUsers(usersData);
    } else {
      setUsers(prev => [...prev, ...usersData]);
    }
    setTotal(resp.count || 0);
    setHasMore(Boolean(resp.next));
    setPage(nextPage);
    setLoading(false);
  };

  // Function to update deal restrictions with delay
  const updateRestrictions = async (userId, restrictedDealTypes, action = '', dealType = '', restrictionId = null, originalRestrictions = []) => {
    try {
      const result = await updateUserDealRestrictions(userId, restrictedDealTypes, action, dealType);
      if (!result.ok) {
        console.error('Failed to update deal restrictions for user');
        // Revert optimistic update on failure using original restrictions
        setUsers(prev => prev.map(r => 
          r.id === restrictionId 
            ? { ...r, restricted_deal_types: originalRestrictions }
            : r
        ));
      } else {
        // Track restriction update only on success
        if (action && dealType) {
          userEventService.trackEvent({
            eventCategory: 'Admin Capabilities',
            eventAction: 'admin_cpro_user_restriction_update',
            eventLabel: 'admin_manage_cpro_users',
          });
        }
      }
    } catch (e) {
      console.error('Error updating deal restrictions');
      // Revert optimistic update on failure using original restrictions
      setUsers(prev => prev.map(r => 
        r.id === restrictionId 
          ? { ...r, restricted_deal_types: originalRestrictions }
          : r
      ));
    }
  };

  const handleRestrictionChange = (restrictionId, dealType, checked) => {
    // Find the restriction object
    const restriction = users.find(r => r.id === restrictionId);
    if (!restriction) return;
    
    const currentRestrictions = restriction.restricted_deal_types || [];
    let newRestrictions;
    const action = checked ? 'added' : 'revoke';
    
    if (checked) {
      // Add the deal type if not already present
      newRestrictions = [...new Set([...currentRestrictions, dealType])];
    } else {
      // Remove the deal type
      newRestrictions = currentRestrictions.filter(type => type !== dealType);
    }
    
    // Optimistic update
    setUsers(prev => prev.map(r => 
      r.id === restrictionId 
        ? { ...r, restricted_deal_types: newRestrictions } 
        : r
    ));
    
    // Delay API call with 500ms timeout
    if (restrictionUpdateTimer.current) {
      clearTimeout(restrictionUpdateTimer.current);
    }
    restrictionUpdateTimer.current = setTimeout(() => {
      updateRestrictions(restriction.user.id, newRestrictions, action, dealType, restrictionId, currentRestrictions);
    }, 500);
  };

  // Debounced search
  const debouncedSearch = useCallback(debounce((value) => {
    loadUsers(1, value);
  }, 300), []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      loadUsers(page + 1, searchTerm);
    }
  };

  const handleShowUserHistory = async (userId) => {
    const isCurrentlyVisible = userHistoryVisible[userId];
    setUserHistoryVisible(prev => ({ ...prev, [userId]: !isCurrentlyVisible }));
    
    // Fetch history every time we show it (reset to page 1)
    if (!isCurrentlyVisible) {

      // Track audit trail view
      userEventService.trackEvent({
        eventCategory: 'Admin Capabilities',
        eventAction: 'admin_cpro_user_audit_viewed',
        eventLabel: 'admin_manage_cpro_users',
      });
      
      setUserAuditLoading(prev => ({ ...prev, [userId]: true }));
      setUserAuditPage(prev => ({ ...prev, [userId]: 1 }));
      try {
        const data = await fetchCProUserAudit(userId, 1, 25);
        const historyArray = Array.isArray(data) ? data : (data?.results || []);
        const hasMore = data?.has_more || (data?.next ? true : false);
        const total = data?.count || historyArray.length;
        setUserAuditTrail(prev => ({ ...prev, [userId]: historyArray }));
        setUserAuditHasMore(prev => ({ ...prev, [userId]: hasMore }));
        setUserAuditTotal(prev => ({ ...prev, [userId]: total }));
      } catch (e) {
        console.error('Failed to fetch user history');
      }
      setUserAuditLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleLoadMoreHistory = async (userId) => {
    const currentPage = userAuditPage[userId] || 1;
    const nextPage = currentPage + 1;
    setUserAuditLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const data = await fetchCProUserAudit(userId, nextPage, 25);
      const newEntries = Array.isArray(data) ? data : (data?.results || []);
      const hasMore = data?.has_more || (data?.next ? true : false);
      setUserAuditTrail(prev => ({ ...prev, [userId]: [...(prev[userId] || []), ...newEntries] }));
      setUserAuditHasMore(prev => ({ ...prev, [userId]: hasMore }));
      setUserAuditPage(prev => ({ ...prev, [userId]: nextPage }));
    } catch (e) {
      console.error('Failed to fetch more user history');
    }
    setUserAuditLoading(prev => ({ ...prev, [userId]: false }));
  };

  useEffect(() => {
    loadUsers(1, '');
    // cleanup timers on unmount
    return () => {
      if (restrictionUpdateTimer.current) {
        clearTimeout(restrictionUpdateTimer.current);
      }
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderRow = (restriction) => {
    const email = restriction?.user?.email?.work || restriction?.user?.work_email || (typeof restriction?.user?.email === 'string' ? restriction?.user?.email : null) || '--';
    const userId = restriction?.user?.id;
    const id = restriction.id;
    const isHistoryVisible = userHistoryVisible[userId];
    const historyData = userAuditTrail[userId] || [];
    const isHistoryLoading = userAuditLoading[userId];
    const auditTotal = userAuditTotal[userId] || 0;

    return (
      <React.Fragment key={id}>
        <div className="cpro-user-row">
          <div className="user-email">{email}</div>
          <div className="deal-restrictions">
            {['sale', 'lease', 'consulting', 'other'].map(dealType => {
              const userRestrictions = restriction.restricted_deal_types || [];
              const isChecked = userRestrictions.includes(dealType);
              return (
                <label key={dealType} className="restriction-checkbox-label">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => handleRestrictionChange(id, dealType, e.target.checked)}
                  />
                  {dealType}
                </label>
              );
            })}
          </div>
          <div className="actions">
            <Button size="xs" onClick={() => handleShowUserHistory(userId)}>{isHistoryVisible ? 'Hide History' : 'View History'}</Button>
          </div>
        </div>
        {isHistoryVisible && (
          <div className="user-history">
            <h5>Audit History for {email}</h5>
            {isHistoryLoading ? (
              <div className="history-loading"><LoadingBar /><span>Loading history…</span></div>
            ) : historyData.length === 0 ? (
              <div className="history-empty">No history entries found.</div>
            ) : (
              <>
                <div className="audit-table">
                  <div className="admin-table-header">
                    <div>MODIFIED ON</div>
                    <div>MODIFIED BY</div>
                    <div>MESSAGE</div>
                  </div>
                  <div className="audit-body">
                    {historyData.map((entry, idx) => (
                      <React.Fragment key={idx}>
                        <div>{entry.modified ? moment(entry.modified).format('MMM DD, YYYY hh:mm A') : '--'}</div>
                        <div>{entry.admin_user_email || '--'}</div>
                        <div>{entry.message}</div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                {historyData.length > 0 && userAuditHasMore[userId] && (
                  <div className="pagination-controls">
                    <span className="results-count">Showing {historyData.length} of {auditTotal} results</span>
                    <div 
                      className={`nd-load-more-button load-more-button ${isHistoryLoading ? 'loading' : ''}`}
                      onClick={() => handleLoadMoreHistory(userId)}
                    >
                      {isHistoryLoading ? 'Loading...' : 'Load More History'}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="admin-manage-deal">   
      <div className="admin-manage-cpro-users">
        <div className="cpro-users-header">
          <h3>ConnectorPro Users</h3>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by email ... "
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {loading && <LoadingBar />}
            <span className="total-count">Total: {total}</span>
          </div>
        </div>
        <div className="cpro-users-table">
          <div className="cpro-table-header">
            <div className="column-email">USER EMAIL</div>
            <div className="column-restrictions">DEAL TYPE RESTRICTION</div>
            <div className="column-actions"></div>
          </div>
          {users.length === 0 && !loading && (
            <EmptyState type="users" title="No CPro users found" />
          )}
          {users.map(renderRow)}
        </div>
        <div className="pagination-section">
          <span className="results-count">Showing {users.length} of {total} results</span>
          {hasMore && (
            <div 
              className={`nd-load-more-button load-more-button ${loading ? 'loading' : ''}`}
              onClick={handleLoadMore}
            >
              {loading ? 'Loading...' : 'Load More'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

AdminManageCProUsers.propTypes = {
  pageSize: PropTypes.number,
  userData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

AdminManageCProUsers.defaultProps = {
  pageSize: 25,
  userData: null,
};

export default AdminManageCProUsers;
